/**
 * Lectura de carpetas arrastradas.
 *
 * Al soltar una carpeta, `dataTransfer.files` trae una entrada que parece un
 * archivo pero es un directorio: subirla falla con un error de red que no
 * explica nada. La forma correcta es recorrer el árbol con la API de entradas
 * del navegador y quedarse con los archivos de verdad, cada uno con la ruta
 * que tenía dentro de la carpeta.
 */

export interface EntradaSubida {
  archivo: File;
  /** Carpetas por sobre el archivo, relativas al destino. Vacío = raíz. */
  ruta: string;
}

/** Tope por subida. Con más que esto conviene partir en tandas. */
export const MAXIMO_ARCHIVOS = 200;

interface EntradaFS {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file(exito: (a: File) => void, error: (e: unknown) => void): void;
  createReader(): {
    readEntries(exito: (entradas: EntradaFS[]) => void, error: (e: unknown) => void): void;
  };
}

const comoArchivo = (entrada: EntradaFS) =>
  new Promise<File | null>((resolver) => entrada.file(resolver, () => resolver(null)));

/** `readEntries` devuelve como máximo 100 por llamada: hay que insistir hasta que vacíe. */
async function leerTodo(lector: ReturnType<EntradaFS['createReader']>): Promise<EntradaFS[]> {
  const todas: EntradaFS[] = [];
  for (;;) {
    const tanda = await new Promise<EntradaFS[]>((resolver) =>
      lector.readEntries(resolver, () => resolver([])),
    );
    if (tanda.length === 0) return todas;
    todas.push(...tanda);
  }
}

async function recorrer(entrada: EntradaFS, ruta: string, salida: EntradaSubida[]): Promise<void> {
  if (salida.length >= MAXIMO_ARCHIVOS) return;

  if (entrada.isFile) {
    const archivo = await comoArchivo(entrada);
    // Los archivos ocultos del sistema (.DS_Store, Thumbs.db) no aportan nada
    // y solo ensucian el repositorio del cliente.
    if (archivo && !archivo.name.startsWith('.')) salida.push({ archivo, ruta });
    return;
  }

  if (entrada.isDirectory) {
    const hijas = await leerTodo(entrada.createReader());
    const dentro = ruta ? `${ruta}/${entrada.name}` : entrada.name;
    for (const hija of hijas) await recorrer(hija, dentro, salida);
  }
}

/**
 * Convierte lo que se soltó en una lista plana de archivos con su ruta.
 * Si el navegador no soporta la API de entradas, cae a los archivos sueltos.
 */
export async function entradasDeArrastre(datos: DataTransfer): Promise<EntradaSubida[]> {
  const items = [...datos.items].filter((i) => i.kind === 'file');
  const soportaEntradas = items.length > 0 && typeof items[0]!.webkitGetAsEntry === 'function';

  if (!soportaEntradas) {
    return [...datos.files].map((archivo) => ({ archivo, ruta: '' }));
  }

  // getAsEntry hay que llamarlo antes de cualquier await: la lista de items se
  // vacía apenas termina el manejador del evento.
  const raices = items
    .map((i) => i.webkitGetAsEntry() as unknown as EntradaFS | null)
    .filter((e): e is EntradaFS => !!e);

  const salida: EntradaSubida[] = [];
  for (const raiz of raices) await recorrer(raiz, '', salida);
  return salida;
}

/**
 * Archivos elegidos con el selector de carpeta (`webkitdirectory`). Ahí el
 * navegador sí entrega la ruta, en `webkitRelativePath`.
 */
export function entradasDeSelector(archivos: FileList): EntradaSubida[] {
  return [...archivos]
    .filter((a) => !a.name.startsWith('.'))
    .map((archivo) => {
      const relativa = (archivo as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
      const carpetas = relativa.split('/').slice(0, -1).join('/');
      return { archivo, ruta: carpetas };
    });
}
